<script setup lang="ts">
import { logCompletion } from '@bosca/analytics-client-browser'
import type { ProfileStudy, Study } from '~/utils/generated/graphql'
import { markCompleted } from '~/utils/mutations'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Loading from '~/assets/images/loading.svg?component'

const router = useRouter()
const props = defineProps<{
  study: Study
  profile: ProfileStudy | null | undefined
}>()
const disabled = ref(false)
const error = ref(false)

const nextSession = computed(() => {
  if (!props.profile?.nextSessionId || props.profile?.nextSessionId == props.study.session?.id) {
    let currentIndex = props.study.sessions.findIndex(s => s.id === props.study.session?.id)
    if (currentIndex === -1) return null
    if (currentIndex + 1 === props.study.sessions.length) {
      currentIndex = -1
    }
    while (currentIndex + 1 < props.study.sessions.length) {
      currentIndex++
      const session = props.study.sessions[currentIndex]
      if (session.id === props.study.session?.id || props.profile?.completedSessionIds?.includes(session.id)) continue
      return session.id
    }
    return null
  }
  return props.profile.nextSessionId
})

const day = computed(() => {
  return props.study.sessions.findIndex(s => s.id === props.study.session?.id) + 1
})

async function onClicked() {
  if (disabled.value) return
  disabled.value = true
  error.value = false
  try {
    await markCompleted(props.study.id, props.study.session?.id)
    if (nextSession.value) {
      await router.push('/studies/' + props.study.slug + '/' + nextSession.value.split('-')[1])
      await logCompletion({
        id: 'next-session',
        type: 'button',
        content: [{ id: props.study.id, type: props.study.__typename || '' }],
        extras: { slug: props.study.slug },
      })
    }
    else {
      await router.push('/studies')
      await logCompletion({
        id: 'finish-session',
        type: 'button',
        content: [{ id: props.study.id, type: props.study.__typename || '' }],
        extras: { slug: props.study.slug },
      })
    }
    window.dispatchEvent(new Event('refresh-header'))
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  catch (e: any) {
    error.value = true
    await logCompletion({
      id: 'finish-session',
      type: 'button',
      content: [{ id: props.study.id, type: props.study.__typename || '' }],
      extras: { slug: props.study.slug, error: 'true', errorMessage: e.toString() },
    })
  }
  finally {
    disabled.value = false
  }
}
</script>

<template>
  <div class="w-full">
    <UAlert
      v-if="error"
      class="mt-16"
      color="red"
      variant="subtle"
      :description="$t('errorCompletingDay')"
    />
    <div class="flex justify-center mt-8">
      <PButton
        v-if="nextSession"
        id="next-session"
        :content="[{ id: study.id, type: study.__typename || '' }]"
        :extras="{ slug: study.slug }"
        class="text-white h-12 flex justify-center items-center rounded-lg bg-blue-700 w-full no-underline hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 font-medium px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        :click="onClicked"
      >
        <Loading
          v-if="disabled"
          class="stroke-white w-4 h-4 animate-spin"
        />
        <span v-else>
          {{ $t('nextSession', { n: day }) }}
        </span>
      </PButton>
      <PButton
        v-else
        id="finish-session"
        :content="[{ id: study.id, type: study.__typename || '' }]"
        :extras="{ slug: study.slug }"
        class="text-white h-12 flex justify-center items-center rounded-lg bg-blue-700 w-full no-underline hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 font-medium px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
        :click="onClicked"
      >
        <Loading
          v-if="disabled"
          class="stroke-white w-4 h-4 animate-spin"
        />
        <span v-else>
          {{ $t('finishSession') }}
        </span>
      </PButton>
    </div>
  </div>
</template>
